/* =============================================================================
   CREATIVE DETONATOR - MAIN JAVASCRIPT
   Common functionality across all pages
   FRONTEND-25: Uses Logger for environment-aware logging
   ============================================================================= */

// FRONTEND-25: Simple logger helper using window.Logger if available
// Note: Uses typeof check because optional chaining doesn't protect against undefined method calls
const mainLog = {
  debug: (...args) => (typeof window.Logger?.debug === 'function' ? window.Logger.debug('main', ...args) : console.debug('[main]', ...args)),
  info: (...args) => (typeof window.Logger?.info === 'function' ? window.Logger.info('main', ...args) : console.log('[main]', ...args)),
  warn: (...args) => (typeof window.Logger?.warn === 'function' ? window.Logger.warn('main', ...args) : console.warn('[main]', ...args)),
  error: (...args) => (typeof window.Logger?.error === 'function' ? window.Logger.error('main', ...args) : console.error('[main]', ...args))
};

// API Configuration
// Auto-detects development (localhost) vs production environment
const API_BASE = window.API_CONFIG_URL || 'https://api.creativedetonator.com';

// Data Store - Fetches from backend API (single source of truth)
const DataStore = {
  triggers: null,
  categories: null,
  _initialized: false,
  _initPromise: null,

  // Single unified loader - fetches both triggers and categories in one request
  async init() {
    // Return cached data if already loaded
    if (this._initialized && this.triggers && this.categories) {
      return;
    }

    // Prevent duplicate concurrent requests
    if (this._initPromise) {
      return this._initPromise;
    }

    this._initPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/triggers`);

        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        this.triggers = data.triggers;
        this.categories = data.categories;
        this._initialized = true;

        mainLog.debug(`DataStore initialized: ${this.triggers.length} triggers, ${this.categories.length} categories`);
      } catch (error) {
        mainLog.error('CRITICAL: Failed to load data from backend:', error.message);
        mainLog.error('Make sure backend is running: cd backend && npm start');
        // Set empty arrays to prevent null errors
        this.triggers = [];
        this.categories = [];
      }
    })();

    return this._initPromise;
  },

  // Backward-compatible methods - call init() internally
  async loadTriggers() {
    await this.init();
    return this.triggers;
  },

  async loadCategories() {
    await this.init();
    return this.categories;
  },

  getBasePath() {
    // Still needed for image/asset paths (not data)
    const path = window.location.pathname;
    // Features pages are 3 levels deep: features/X/pages/
    if (path.includes('/features/triggers/pages/') || path.includes('/features/method/pages/')) {
      return '../../../';
    } else if (path.includes('/pages/')) {
      return '../';
    }
    return '';
  },

  getTriggerByCode(code) {
    if (!this.triggers) return null;
    return this.triggers.find(t => t.code === code);
  },

  getTriggersByCategory(categoryCode) {
    if (!this.triggers) return [];
    return this.triggers.filter(t => t.category === categoryCode);
  },

  getCategoryByCode(code) {
    if (!this.categories) return null;
    return this.categories.find(c => c.code === code);
  },

  // FRONTEND-31: Get category color from loaded data
  getCategoryColor(code) {
    if (!code) return '#000000';
    const categoryCode = code.charAt(0).toUpperCase();
    const category = this.getCategoryByCode(categoryCode);
    return category?.color || CategoryColors[categoryCode] || '#000000';
  }
};

// =============================================================================
// TRIGGER DATA - Category Colors & Image Mappings
// =============================================================================
// FRONTEND-31: Colors are now sourced from DataStore.categories (backend JSON)
// CSS variables are generated to frontend/css/generated/_colors.css
// This object is populated dynamically after DataStore.init() completes
//
// For ES6 modules: import { CategoryColors } from './core/trigger-data.js'
// For non-module scripts: window.CategoryColors (available after DataStore.init)
// =============================================================================

// Minimal fallback for when DataStore hasn't loaded yet or fails to load
// Once DataStore initializes, this gets populated from categories.json
const CategoryColors = window.CategoryColors || {
  // FALLBACK VALUES - These will be overwritten by DataStore.init()
  // Kept minimal for backward compatibility if scripts access before init
  A: '#FF00FF', B: '#FF6600', C: '#00FFFF', D: '#00FF00', E: '#9900FF',
  F: '#FFFF00', G: '#0066FF', H: '#FF0000', I: '#FF6B9D', J: '#4ECDC4',
  K: '#9B59B6', L: '#E74C3C', M: '#2ECC71', N: '#1ABC9C', O: '#F39C12'
};

/**
 * Get category color dynamically from loaded data or CSS variable
 * @param {string} code - Category code (A-O) or trigger code (e.g., 'A1')
 * @returns {string} - Hex color (default: #000000)
 */
function getCategoryColor(code) {
  if (!code) return '#000000';
  const categoryCode = code.charAt(0).toUpperCase();

  // First, try from loaded DataStore categories (most up-to-date)
  if (DataStore.categories && DataStore.categories.length > 0) {
    const category = DataStore.categories.find(c => c.code === categoryCode);
    if (category?.color) return category.color;
  }

  // Fallback to CategoryColors object
  return CategoryColors[categoryCode] || '#000000';
}

/**
 * Populate CategoryColors from DataStore.categories
 * Called automatically after DataStore.init() completes
 */
function populateCategoryColorsFromDataStore() {
  if (!DataStore.categories || DataStore.categories.length === 0) return;

  DataStore.categories.forEach(category => {
    if (category.code && category.color) {
      CategoryColors[category.code] = category.color;
    }
  });

  mainLog.debug('CategoryColors populated from DataStore');
}

const TriggerImageFiles = window.TriggerImageFiles || {
  'A1': 'A1-The-Impossible-Visual.png', 'A2': 'A2-The-Wrong-Context.png',
  'A3': 'A3-The-Scale-Violation.png', 'A4': 'A4-The-Time-Crime.png',
  'A5': 'A5-The-Role-Reversal.png', 'B1': 'B1-The-Guilt-Trigger.png',
  'B2': 'B2-The-Nostalgia-Bomb.png', 'B3': 'B3-The-Pride-Amplifier.png',
  'B4': 'B4-The-Fear-Whisper.png', 'B5': 'B5-The-Joy-Spike.png',
  'B6': 'B6-The-Empathy-Bridge.png', 'C1': 'C1-The-Missing-Piece.png',
  'C2': 'C2-The-Double-Meaning.png', 'C3': 'C3-The-Reframe.png',
  'C4': 'C4-The-Category-Crime.png', 'C5': 'C5-The-Literal-Translation.png',
  'C6': 'C6-The-Extreme-Hypothetical.png', 'D1': 'D1-The-Invitation.png',
  'D2': 'D2-The-Blank-Space.png', 'D3': 'D3-The-Challenge.png',
  'D4': 'D4-The-Reward-Loop.png', 'E1': 'E1-The-Origin-Myth.png',
  'E2': 'E2-The-Enemy.png', 'E3': 'E3-The-Quest.png',
  'E4': 'E4-The-Transformation.png', 'E5': 'E5-The-Secret.png',
  'F1': 'F1-The-Icon.png', 'F2': 'F2-The-Color-Code.png',
  'F3': 'F3-The-Sound-Signature.png', 'F4': 'F4-The-Ritual.png',
  'G1': 'G1-The-Format-Flip.png', 'G2': 'G2-The-Platform-Hack.png',
  'G3': 'G3-The-Timing-Trick.png', 'G4': 'G4-The-Context-Bomb.png',
  'H1': 'H1-The-Profanity.png', 'H2': 'H2-The-Sacred-Cow.png',
  'H3': 'H3-The-Danger-Zone.png', 'I1': 'I1-The-Synesthesia-Switch.png',
  'I2': 'I2-The-Phantom-Sensation.png', 'I3': 'I3-The-Ambient-Invasion.png',
  'I4': 'I4-The-Kinetic-Freeze.png', 'J1': 'J1-The-Exclusion-Paradox.png',
  'J2': 'J2-The-Social-Proof-Warp.png', 'J3': 'J3-The-Status-Redistribution.png',
  'J4': 'J4-The-Witnessed-Moment.png', 'K1': 'K1-The-Mirror-Ambush.png',
  'K2': 'K2-The-Future-Self.png', 'K3': 'K3-The-Shadow-Acknowledgment.png',
  'L1': 'L1-The-Delayed-Reveal.png', 'L2': 'L2-The-Almost-Moment.png',
  'L3': 'L3-The-Relief-Valve.png', 'L4': 'L4-The-Productive-Discomfort.png',
  'M1': 'M1-The-Authority-Loan.png', 'M2': 'M2-The-Occasion-Creation.png',
  'M3': 'M3-The-Effort-Offset.png', 'M4': 'M4-The-Collective-Absolution.png',
  'N1': 'N1-The-Fourth-Wall-Demolition.png', 'N2': 'N2-The-Process-Exposure.png',
  'N3': 'N3-The-Failure-Feature.png', 'N4': 'N4-The-Consumer-as-Creator.png',
  'O1': 'O1-The-Unstoppable-Force.png', 'O2': 'O2-The-First-Domino.png',
  'O3': 'O3-The-Counter-Current.png', 'O4': 'O4-The-Gravitational-Pull.png'
};

// Get trigger image path from code
const getTriggerImagePathFromCode = function(code, basePath = '') {
  const filename = TriggerImageFiles[code];
  return filename ? `${basePath}assets/images/triggers/${filename}` : null;
};

// Logo Pressed State
function initLogoPressedState() {
  const logo = document.querySelector('.logo');
  const logoIcon = document.querySelector('.logo-icon');
  if (!logo || !logoIcon) return;

  const normalSrc = logoIcon.dataset.normal;
  const pressedSrc = logoIcon.dataset.pressed;

  if (!normalSrc || !pressedSrc) return;

  const setPressed = () => {
    logoIcon.src = pressedSrc;
    logoIcon.classList.add('pressed');
  };

  const setNormal = () => {
    logoIcon.src = normalSrc;
    logoIcon.classList.remove('pressed');
  };

  logo.addEventListener('mousedown', setPressed);
  logo.addEventListener('mouseup', setNormal);
  logo.addEventListener('mouseleave', setNormal);

  // Touch support
  logo.addEventListener('touchstart', setPressed);
  logo.addEventListener('touchend', setNormal);
}

// Mobile Menu
function initMobileMenu() {
  const toggle = document.querySelector('.mobile-menu-toggle');
  const menu = document.querySelector('.mobile-menu');

  if (toggle && menu) {
    toggle.addEventListener('click', () => {
      menu.classList.toggle('active');
      toggle.classList.toggle('active');
    });
  }
}

// Copy to Clipboard
function copyToClipboard(text, button) {
  navigator.clipboard.writeText(text).then(() => {
    const originalText = button.textContent;
    button.textContent = 'COPIED!';
    setTimeout(() => {
      button.textContent = originalText;
    }, 2000);
  });
}

// Initialize copy buttons
function initCopyButtons() {
  document.querySelectorAll('.btn-copy').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.copy;
      let text = '';

      if (target === 'question') {
        const questionEl = document.querySelector('.trigger-question');
        text = questionEl ? questionEl.textContent.trim() : '';
      }

      if (text) {
        copyToClipboard(text, btn);
      }
    });
  });
}

// Render Trigger Card
function renderTriggerCard(trigger, linkPath = '') {
  const category = DataStore.getCategoryByCode(trigger.category);
  const color = CategoryColors[trigger.category] || '#000000';

  return `
    <a href="${linkPath}trigger.html?code=${trigger.code}" class="trigger-card" style="--cat-color: ${color}">
      <div class="trigger-card-illustration">
        ${getTriggerIcon(trigger.code)}
      </div>
      <div class="trigger-card-code">${trigger.code}</div>
      <div class="trigger-card-title">${trigger.name.toUpperCase()}</div>
    </a>
  `;
}

// Render Category Card
function renderCategoryCard(category, linkPath = '') {
  return `
    <a href="${linkPath}features/triggers/pages/index.html#category-${category.code.toLowerCase()}" class="category-card">
      <div class="category-card-badge" style="background-color: ${category.color}">${category.code}</div>
      <div class="category-card-title">${category.name.toUpperCase()}</div>
      <div class="category-card-count">${category.triggerCount} triggers</div>
    </a>
  `;
}

// Get Trigger Icon (placeholder - you can replace with actual icons)
function getTriggerIcon(code) {
  const icons = {
    A1: '&#10024;', // Sparkle (impossible)
    A2: '&#128205;', // Pin (wrong context)
    A3: '&#128200;', // Chart (scale)
    A4: '&#9200;', // Clock (time)
    A5: '&#128260;', // Arrows (reversal)
    B1: '&#128073;', // Pointing (guilt)
    B2: '&#128250;', // TV (nostalgia)
    B3: '&#127942;', // Trophy (pride)
    B4: '&#128064;', // Eyes (fear)
    B5: '&#127881;', // Party (joy)
    B6: '&#128149;', // Heart (empathy)
    C1: '&#129513;', // Puzzle
    C2: '&#129704;', // Mirror
    C3: '&#128256;', // Frame
    C4: '&#127912;', // Art
    C5: '&#128172;', // Speech
    C6: '&#9878;', // Scale
    D1: '&#9995;', // Hand
    D2: '&#127922;', // Dice
    D3: '&#10062;', // Complete
    D4: '&#127873;', // Gift
    E1: '&#127916;', // Film
    E2: '&#128214;', // Book
    E3: '&#128483;', // Mic
    E4: '&#127793;', // Seed (origin)
    E5: '&#128302;', // Crystal ball
    F1: '&#9888;', // Warning
    F2: '&#128288;', // ABC
    F3: '&#128202;', // Chart
    F4: '&#127912;', // Art
    G1: '&#128196;', // Page
    G2: '&#127758;', // Globe
    G3: '&#128257;', // Sequence
    G4: '&#128230;', // Package
    H1: '&#9888;', // Warning
    H2: '&#9876;', // Sword
    H3: '&#128578;', // Smile
    // Sensory Translators (I)
    I1: '&#127926;', // Musical note (synesthesia)
    I2: '&#128075;', // Waving hand (phantom sensation)
    I3: '&#127787;', // Sun behind cloud (ambient)
    I4: '&#9208;', // Pause button (kinetic freeze)
    // Social Physics (J)
    J1: '&#128274;', // Lock (exclusion)
    J2: '&#128101;', // People (social proof)
    J3: '&#9878;', // Balance scale (status)
    J4: '&#128065;', // Eye (witnessed)
    // Identity Provocations (K)
    K1: '&#129704;', // Mirror (ambush)
    K2: '&#128640;', // Rocket (future self)
    K3: '&#127769;', // Crescent moon (shadow)
    // Tension Architecture (L)
    L1: '&#8987;', // Hourglass (delayed reveal)
    L2: '&#128168;', // Dash (almost moment)
    L3: '&#128167;', // Droplet (relief valve)
    L4: '&#9888;', // Warning (productive discomfort)
    // Permission Engines (M)
    M1: '&#127891;', // Graduation cap (authority)
    M2: '&#127874;', // Birthday cake (occasion)
    M3: '&#127941;', // Medal (effort offset)
    M4: '&#128591;', // Folded hands (absolution)
    // Reality Ruptures (N)
    N1: '&#128084;', // Clapperboard (fourth wall)
    N2: '&#128295;', // Wrench (process)
    N3: '&#10060;', // Cross mark (failure)
    N4: '&#9999;', // Pencil (consumer creator)
    // Momentum Mechanics (O)
    O1: '&#127754;', // Wave (unstoppable force)
    O2: '&#128178;', // Dollar (first domino)
    O3: '&#128040;', // Fish (counter-current)
    O4: '&#127759;' // Globe (gravitational pull)
  };
  return icons[code] || '&#9733;';
}

// Format text helper
function formatText(text) {
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  initLogoPressedState();
  initMobileMenu();
  initCopyButtons();
});

// Export for use in other scripts
window.DataStore = DataStore;
window.CategoryColors = CategoryColors;
window.TriggerImageFiles = TriggerImageFiles;
window.getTriggerImagePathFromCode = getTriggerImagePathFromCode;
window.getCategoryColor = getCategoryColor;  // FRONTEND-31: Dynamic color lookup
window.renderTriggerCard = renderTriggerCard;
window.renderCategoryCard = renderCategoryCard;
window.getTriggerIcon = getTriggerIcon;
window.copyToClipboard = copyToClipboard;
window.initCopyButtons = initCopyButtons;

// ============================================================================
// APP NAMESPACE INTEGRATION (FRONTEND-22)
// ============================================================================

/**
 * Connect DataStore to App namespace when App is available
 * This bridges the legacy DataStore to the new App.data API
 */
function initAppNamespace() {
  if (window.App) {
    // Populate App.data from DataStore
    window.App.data.triggers = DataStore.triggers;
    window.App.data.categories = DataStore.categories;

    // Add DataStore methods to App
    window.App.data.getTriggerByCode = (code) => DataStore.getTriggerByCode(code);
    window.App.data.getTriggersByCategory = (cat) => DataStore.getTriggersByCategory(cat);
    window.App.data.getCategoryByCode = (code) => DataStore.getCategoryByCode(code);
    window.App.data.getBasePath = () => DataStore.getBasePath();
    // FRONTEND-31: Add getCategoryColor to App.data
    window.App.data.getCategoryColor = getCategoryColor;

    // Register DataStore as a module
    window.App.registerModule('dataStore', {
      loadAll: () => DataStore.init(),
      getTriggers: () => DataStore.triggers,
      getCategories: () => DataStore.categories,
      getCategoryColor: getCategoryColor
    });

    mainLog.debug('Connected DataStore to App namespace');
  }
}

// Add loadAll method to DataStore for boot.js compatibility
DataStore.loadAll = DataStore.init;
DataStore.getTriggers = function() { return this.triggers; };
DataStore.getCategories = function() { return this.categories; };

// Initialize App namespace after DataStore loads
const originalInit = DataStore.init.bind(DataStore);
DataStore.init = async function() {
  await originalInit();
  // FRONTEND-31: Populate CategoryColors from loaded data
  populateCategoryColorsFromDataStore();
  initAppNamespace();
  return this;
};
